const API_URL = import.meta.env.VITE_API_URL || 'https://localhost:5001/api';
export const SOPHIE_TEAM_OVERRIDE_STORAGE_KEY = 'sophie-team-override-id';
const TEAM_OVERRIDE_HEADER = 'X-Team-Override';

export const buildAuthHeaders = (): Record<string, string> => {
  const accessToken = localStorage.getItem('sb-access-token');
  const refreshToken = localStorage.getItem('sb-refresh-token');
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  };
  if (accessToken && refreshToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
    headers['X-Refresh-Token'] = refreshToken;
    try {
      const teamOverrideId = sessionStorage.getItem(SOPHIE_TEAM_OVERRIDE_STORAGE_KEY)?.trim();
      if (teamOverrideId) {
        headers[TEAM_OVERRIDE_HEADER] = teamOverrideId;
      }
    } catch (storageError) {
      console.debug('Failed to read team override id', storageError);
    }
  }
  return headers;
};

const handleAuthRedirect = (status: number) => {
  if (status === 401) {
    try {
      localStorage.removeItem('sb-access-token');
      localStorage.removeItem('sb-refresh-token');
      sessionStorage.removeItem(SOPHIE_TEAM_OVERRIDE_STORAGE_KEY);
    } catch (storageError) {
      console.debug('Failed to clear auth tokens', storageError);
    }
    if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/signin')) {
      window.location.assign('/signin');
    }
  }
};

export const apiJson = async <T = unknown>(endpoint: string, options: RequestInit = {}): Promise<T> => {
  const defaultOptions: RequestInit = {
    mode: 'cors',
    headers: buildAuthHeaders(),
  };

  const response = await fetch(`${API_URL}${endpoint}`, {
    ...defaultOptions,
    ...options,
    headers: {
      ...(defaultOptions.headers || {}),
      ...(options.headers || {}),
    },
  });

  if (!response.ok) {
    handleAuthRedirect(response.status);
    const errorText = await response.text();
    let message = `API request failed (${response.status})`;
    if (errorText) {
      try {
        const error = JSON.parse(errorText) as { message?: string; error?: string };
        message = error.message || error.error || message;
      } catch {
        message = errorText || message;
      }
    }
    throw new Error(message);
  }

  if (response.status === 204 || response.status === 205) {
    return undefined as T;
  }

  const bodyText = await response.text();
  if (!bodyText.trim()) {
    return undefined as T;
  }

  return JSON.parse(bodyText) as T;
};

export const apiRaw = async (endpoint: string, options: RequestInit = {}): Promise<Response> => {
  const defaultOptions: RequestInit = {
    credentials: 'include',
    mode: 'cors',
    headers: buildAuthHeaders(),
  };

  // Merge headers, but if sending FormData, let the browser set Content-Type with boundary
  const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
  const mergedHeaders: Record<string, string> = {
    ...(defaultOptions.headers as Record<string, string>),
    ...(options.headers as Record<string, string>),
  };
  if (isFormData && mergedHeaders['Content-Type']) {
    delete mergedHeaders['Content-Type'];
  }

  const response = await fetch(`${API_URL}${endpoint}`, {
    ...defaultOptions,
    ...options,
    headers: mergedHeaders,
  });

  if (!response.ok) {
    handleAuthRedirect(response.status);
    try {
      const error = await response.json();
      throw new Error(error.message || error.error || `API request failed (${response.status})`);
    } catch (e) {
      throw e instanceof Error ? e : new Error(`API request failed (${response.status})`);
    }
  }

  return response;
};


